<template>
  <HFetchQuery :query="reviewsQuery">
    <template #default="{ data: pipelineReviews }">
      <div v-if="pipelineReviews.length">
        <section v-if="asyncReviews(pipelineReviews).length" class="pb-4">
          <h3 class="mb-2 text-xs font-medium uppercase tracking-[0.08em] text-gray-500">Async Metrics</h3>
          <div class="flex flex-wrap gap-3">
            <FollowThroughRate :reviews="asyncReviews(pipelineReviews)" />
          </div>
        </section>
        <ul role="list" class="-mx-2 flex flex-col gap-0.5">
          <li v-for="review in pipelineReviews" :key="review.id">
            <router-link
              :to="pipelineReviewUrl(review)"
              class="group flex items-center gap-3 rounded-md px-2 py-2.5 transition-colors hover:bg-slate-50"
            >
              <div class="flex h-9 w-9 shrink-0 items-center justify-center">
                <ScoreIcon v-if="review.status === 'done' && review.result" :score="review.result.score" size="md" />
                <vue-feather
                  v-else-if="review.status === 'done'"
                  type="check-circle"
                  size="22"
                  class="text-slate-400"
                />
                <vue-feather v-else-if="review.status === 'in_progress'" type="clock" size="22" class="text-blue-600" />
                <vue-feather
                  v-else-if="review.status === 'expired'"
                  type="alert-circle"
                  size="22"
                  class="text-red-600"
                />
              </div>
              <div class="flex min-w-0 flex-1 flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                <div class="flex items-baseline gap-2">
                  <span class="text-sm font-medium text-slate-900">
                    {{ formatDate(review.created_at, 'MMMM dd, yyyy') }}
                  </span>
                  <span class="text-[10px] font-medium uppercase tracking-[0.08em] text-slate-400">
                    {{ review.review_type }}
                  </span>
                </div>
                <StatusBadge :status="review.status" class="self-start sm:self-auto" />
              </div>
              <div
                class="flex shrink-0 items-center gap-0.5 text-sm font-medium text-slate-500 transition-colors group-hover:text-slate-900"
              >
                {{ buttonLabel(review) }}
                <ChevronRight class="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </div>
            </router-link>
          </li>
        </ul>
      </div>
      <div v-else class="py-6 text-center text-sm text-gray-500">This teammate hasn't had any pipeline reviews.</div>
    </template>
  </HFetchQuery>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router';
import { ChevronRight } from 'lucide-vue-next';
import ScoreIcon from '@/components/shared/pipeline_reviews/ScoreIcon.vue';
import DateUtility from '@/utilities/dates';
import { Api } from '@/api';
import type { PipelineReviewListResponse } from '@/api/pipeline_reviews';
import HFetchQuery from '@/lib/hfetch/HFetchQuery.vue';
import { useHFetchQuery } from '@/lib/hfetch/useHFetchQuery';
import StatusBadge from '@/components/shared/pipeline_reviews/StatusBadge.vue';
import FollowThroughRate from '@/components/shared/pipeline_reviews/FollowThroughRate.vue';

const route = useRoute();

const reviewsQuery = useHFetchQuery(() => Api.PipelineReviews.listPipelineReviews(Number(route.params.id)));

const { formatDate } = DateUtility;

const isReadyForReview = (review: PipelineReviewListResponse) =>
  review.review_type === 'async' && review.stage === 'ready_for_review';

const asyncReviews = (reviews: PipelineReviewListResponse[]) => reviews.filter(r => r.review_type === 'async');

const buttonLabel = (review: PipelineReviewListResponse) => (isReadyForReview(review) ? 'Review' : 'View');

const pipelineReviewUrl = (pipelineReview: PipelineReviewListResponse) => {
  if (isReadyForReview(pipelineReview)) {
    return `/team/${route.params.id}/pipeline_reviews/${pipelineReview.id}/edit`;
  }
  if (pipelineReview.contact_event) {
    return `/contact_events/${pipelineReview.contact_event.id}/pipeline_review`;
  }
  return `/team/${route.params.id}/pipeline_reviews/${pipelineReview.id}`;
};
</script>
